import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Package,
  Loader2,
  CheckCircle2,
  LayoutList,
  ScrollText,
  Cloud,
  CloudOff,
} from 'lucide-react';
import { productsApi } from '@/api/productsApi';
import { productDraftsApi } from '@/api/productDraftsApi';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import { uploadApi } from '@/api/uploadApi';
import { handleApiError, getApiErrorDetails } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { usePreferencesStore } from '@/store/preferencesStore';
import type { Product } from '@/types/api';
import { SinglePageLayout } from './SinglePageLayout';
import { StepBasics } from './StepBasics';
import { StepCollection } from './StepCollection';
import { StepItemType } from './StepItemType';
import { StepMaterials } from './StepMaterials';
import { StepPricingInventory } from './StepPricingInventory';
import { StepProductionMethods } from './StepProductionMethods';
import { StepVariants } from './StepVariants';
import { useProductFormDraft } from './useProductFormDraft';
import {
  WIZARD_STEPS,
  createEmptyProductFormState,
  normalizeWizardStep,
  productToFormState,
  stateToCreateRequest,
  stateToDraftPayload,
  validateVariantNames,
  type ProductFormErrors,
  type ProductFormStep,
  type ProductFormState,
} from './types';

interface ProductFormShellProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  product?: Product | null;
  mode?: 'create' | 'edit' | 'duplicate';
  draftId?: string | null;
  onDraftIdChange?: (id: string) => void;
}

export function ProductFormShell({
  isOpen,
  onClose,
  onSuccess,
  product,
  mode: propMode = 'create',
  draftId: initialDraftId,
  onDraftIdChange,
}: ProductFormShellProps) {
  const queryClient = useQueryClient();
  const isEditMode = propMode === 'edit';
  const isDuplicateMode = propMode === 'duplicate';
  const draftEnabled = !isEditMode && !isDuplicateMode;

  const { productFormMode, setProductFormMode } = usePreferencesStore();
  const [formMode, setFormMode] = useState<'wizard' | 'single'>(productFormMode);
  const [activeStep, setActiveStep] = useState<ProductFormStep>('basics');
  const [errors, setErrors] = useState<ProductFormErrors>({});
  const [editState, setEditState] = useState(createEmptyProductFormState());

  const { data: productionMethods } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
    enabled: isOpen,
  });

  const buildPayload = useCallback(
    (formState: ProductFormState) =>
      stateToDraftPayload(formState, productionMethods ?? []),
    [productionMethods]
  );

  const handleDraftLoaded = useCallback(
    (draft: { currentStep?: string | null; formMode?: 'wizard' | 'single' | null }) => {
      if (draft.currentStep) {
        setActiveStep(normalizeWizardStep(draft.currentStep));
      }
      if (draft.formMode === 'wizard' || draft.formMode === 'single') {
        setFormMode(draft.formMode);
      }
    },
    []
  );

  const {
    state: draftState,
    updateState: updateDraftState,
    draftId,
    saveStatus,
    lastSavedAt,
    isLoadingDraft,
    resetState,
    saveNow,
  } = useProductFormDraft({
    draftId: initialDraftId,
    enabled: draftEnabled && isOpen,
    formMode,
    currentStep: activeStep,
    onDraftIdChange,
    buildPayload,
    onDraftLoaded: handleDraftLoaded,
  });

  const state = draftEnabled ? draftState : editState;
  const updateState = draftEnabled ? updateDraftState : setEditState;

  useEffect(() => {
    if (!isOpen) return;
    if (!initialDraftId) {
      setFormMode(productFormMode);
      setActiveStep('basics');
    }
    setErrors({});
  }, [isOpen, productFormMode, initialDraftId]);

  useEffect(() => {
    if (!isOpen || draftEnabled) return;
    if (product) {
      setEditState(productToFormState(product, { duplicate: isDuplicateMode }));
    } else {
      setEditState(createEmptyProductFormState());
    }
  }, [isOpen, product, isDuplicateMode, draftEnabled]);

  useEffect(() => {
    if (!draftEnabled && !product && isOpen) {
      setEditState(createEmptyProductFormState());
    }
  }, [draftEnabled, product, isOpen]);

  useEffect(() => {
    if (!isEditMode && state.description.trim()) {
      updateState((prev) => {
        if (prev.variants.length === 0) return prev;
        const first = prev.variants[0];
        if (first.name === 'Standard' || first.name === '') {
          return {
            ...prev,
            variants: [
              { ...first, name: prev.description.trim() || 'Standard' },
              ...prev.variants.slice(1),
            ],
          };
        }
        return prev;
      });
    }
  }, [state.description, isEditMode, updateState]);

  async function finalizeProductMedia(productId: string, formState: ProductFormState) {
    const urls = [
      ...(formState.primaryImageUrl ? [formState.primaryImageUrl] : []),
      ...formState.imageUrls.filter((url) => url !== formState.primaryImageUrl),
    ];
    for (const url of urls) {
      try {
        await uploadApi.finalizeMedia({
          url,
          entityType: 'PRODUCT',
          entityId: productId,
          displayName: formState.name,
          primary: url === formState.primaryImageUrl,
        });
      } catch (error) {
        console.warn('Product photo saved but could not be finalized', error);
        setErrors((prev) => ({
          ...prev,
          submit: 'Product saved, but one or more photos could not be renamed yet. They remain in the Media Library.',
        }));
      }
    }
  }

  const createMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: async (createdProduct) => {
      await finalizeProductMedia(createdProduct.id, state);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['product-drafts'] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: unknown) => handleSubmitError(error),
  });

  const updateMutation = useMutation({
    mutationFn: (data: ReturnType<typeof stateToCreateRequest>) =>
      productsApi.update(product!.id, data),
    onSuccess: async (updatedProduct) => {
      await finalizeProductMedia(updatedProduct.id, state);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: unknown) => handleSubmitError(error),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => productDraftsApi.publish(id),
    onSuccess: async (publishedProduct) => {
      await finalizeProductMedia(publishedProduct.id, state);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['product-drafts'] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: unknown) => handleSubmitError(error),
  });

  function handleSubmitError(error: unknown) {
    const errorDetails = getApiErrorDetails(error);
    if (errorDetails.isValidationError && Object.keys(errorDetails.fieldErrors).length > 0) {
      const fieldErrors: ProductFormErrors = {};
      Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
        if (field === 'name') fieldErrors.name = message;
        else if (field === 'basePrice') fieldErrors.basePrice = message;
      });
      setErrors((prev) => ({ ...prev, ...fieldErrors, submit: 'Please fix the validation errors below.' }));
    } else {
      setErrors((prev) => ({ ...prev, submit: handleApiError(error) }));
    }
  }

  const validateStep = (step: ProductFormStep): boolean => {
    if (step === 'basics') {
      if (!state.name.trim()) {
        setErrors((prev) => ({ ...prev, name: 'Product name is required' }));
        return false;
      }
      setErrors((prev) => {
        const { name: _name, ...rest } = prev;
        return rest;
      });
    }
    if (step === 'itemType' && !isEditMode) {
      if (!state.itemTypeId) {
        setErrors((prev) => ({ ...prev, itemTypeId: 'Item type is required' }));
        return false;
      }
      setErrors((prev) => {
        const { itemTypeId: _itemTypeId, ...rest } = prev;
        return rest;
      });
    }
    return true;
  };

  const validate = (): boolean => {
    const nextErrors: ProductFormErrors = {};
    if (!isEditMode && !state.itemTypeId) {
      nextErrors.itemTypeId = 'Item type is required';
    }
    if (!state.name.trim()) nextErrors.name = 'Product name is required';
    if (!state.basePrice || parseFloat(state.basePrice) < 0) {
      nextErrors.basePrice = 'Valid price is required';
    }
    const variantError = validateVariantNames(state.variants);
    if (variantError) nextErrors.variants = variantError;
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleModeChange = (mode: 'wizard' | 'single') => {
    setFormMode(mode);
    setProductFormMode(mode);
  };

  const handleClose = () => {
    resetState();
    setEditState(createEmptyProductFormState());
    setErrors({});
    setActiveStep('basics');
    createMutation.reset();
    updateMutation.reset();
    publishMutation.reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const request = stateToCreateRequest(state, productionMethods ?? []);

    if (isEditMode) {
      updateMutation.mutate(request);
      return;
    }

    if (draftEnabled && draftId) {
      await saveNow();
      publishMutation.mutate(draftId);
      return;
    }

    createMutation.mutate(request);
  };

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.id === activeStep);
  const isPending =
    createMutation.isPending || updateMutation.isPending || publishMutation.isPending;

  const saveIndicator = useMemo(() => {
    if (!draftEnabled) return null;
    if (saveStatus === 'saving' || saveStatus === 'pending') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-warm-sand">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Saving draft...
        </span>
      );
    }
    if (saveStatus === 'saved' && lastSavedAt) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-success-400">
          <Cloud className="w-3.5 h-3.5" />
          Draft saved {lastSavedAt.toLocaleTimeString()}
        </span>
      );
    }
    if (saveStatus === 'error') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-danger-400">
          <CloudOff className="w-3.5 h-3.5" />
          Draft save failed
        </span>
      );
    }
    return null;
  }, [draftEnabled, saveStatus, lastSavedAt]);

  const renderWizardStep = () => {
    switch (activeStep) {
      case 'basics':
        return <StepBasics state={state} updateState={updateState} nameError={errors.name} />;
      case 'itemType':
        return <StepItemType state={state} updateState={updateState} error={errors.itemTypeId} />;
      case 'collection':
        return <StepCollection state={state} updateState={updateState} />;
      case 'productionMethods':
        return <StepProductionMethods state={state} updateState={updateState} />;
      case 'materials':
        return <StepMaterials state={state} updateState={updateState} isEditMode={isEditMode} />;
      case 'variants':
        return <StepVariants state={state} updateState={updateState} error={errors.variants} />;
      case 'pricing':
        return (
          <StepPricingInventory
            state={state}
            updateState={updateState}
            priceError={errors.basePrice}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm"
        onClick={!isPending ? handleClose : undefined}
      />

      <div className="relative w-full sm:max-w-3xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden rounded-none sm:rounded-2xl bg-white dark:bg-charcoal-900 border-0 sm:border border-gray-200 dark:border-charcoal-700 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-glow flex-shrink-0">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-charcoal-900" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-bold text-gray-900 dark:text-warm-cream truncate">
                {isEditMode
                  ? 'Edit Product'
                  : isDuplicateMode
                    ? 'Duplicate Product'
                    : 'Add New Product'}
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-warm-sand truncate">
                  {isEditMode ? 'Update product details' : 'Create a product with inventory tracking'}
                </p>
                {saveIndicator}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-charcoal-700 rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        {!isEditMode && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-800/50 flex-shrink-0">
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-charcoal-600 p-0.5">
              <button
                type="button"
                onClick={() => handleModeChange('wizard')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  formMode === 'wizard'
                    ? 'bg-amber-450/15 text-amber-600 dark:text-amber-400'
                    : 'text-gray-600 dark:text-warm-sand'
                }`}
              >
                <LayoutList className="w-4 h-4" />
                Wizard
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('single')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  formMode === 'single'
                    ? 'bg-amber-450/15 text-amber-600 dark:text-amber-400'
                    : 'text-gray-600 dark:text-warm-sand'
                }`}
              >
                <ScrollText className="w-4 h-4" />
                Single Page
              </button>
            </div>
          </div>
        )}

        {formMode === 'wizard' && !isEditMode && (
          <div className="flex items-center gap-1 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-charcoal-700 overflow-x-auto flex-shrink-0">
            {WIZARD_STEPS.map((step, idx) => {
              const isActive = activeStep === step.id;
              const isPast = stepIndex > idx;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => setActiveStep(step.id)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                    ${
                      isActive
                        ? 'bg-amber-450/10 text-amber-450'
                        : isPast
                          ? 'text-success-400 hover:bg-gray-100 dark:hover:bg-charcoal-700'
                          : 'text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-charcoal-700'
                    }
                  `}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      isActive
                        ? 'bg-amber-450 text-charcoal-900'
                        : isPast
                          ? 'bg-success-500/20 text-success-400'
                          : 'bg-gray-100 dark:bg-charcoal-700'
                    }`}
                  >
                    {isPast ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                  </div>
                  {step.label}
                </button>
              );
            })}
          </div>
        )}

        {(errors.submit || createMutation.isError || updateMutation.isError) && (
          <div className="mx-4 sm:mx-6 mt-4 flex-shrink-0">
            <ErrorAlert
              title="Failed to save product"
              message={errors.submit}
              severity="error"
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {isLoadingDraft ? (
              <div className="flex items-center justify-center py-16 text-gray-600 dark:text-warm-sand">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading draft...
              </div>
            ) : formMode === 'single' || isEditMode ? (
              <SinglePageLayout state={state} updateState={updateState} errors={errors} isEditMode={isEditMode} />
            ) : (
              <div className="animate-fade-in">{renderWizardStep()}</div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850 flex-shrink-0">
            {formMode === 'wizard' && !isEditMode ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setActiveStep(WIZARD_STEPS[Math.max(0, stepIndex - 1)].id)
                  }
                  disabled={stepIndex === 0 || isPending}
                  className="btn-secondary"
                >
                  Back
                </button>
                {stepIndex < WIZARD_STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!validateStep(activeStep)) return;
                      setActiveStep(WIZARD_STEPS[Math.min(WIZARD_STEPS.length - 1, stepIndex + 1)].id);
                    }}
                    className="btn-primary"
                  >
                    Continue
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button type="button" onClick={handleClose} className="btn-secondary" disabled={isPending}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary min-w-[140px]" disabled={isPending}>
                      {isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2 inline" />
                          {draftId ? 'Publish Product' : 'Create Product'}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <button type="button" onClick={handleClose} className="btn-secondary" disabled={isPending}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary min-w-[140px]" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2 inline" />
                      {isEditMode ? 'Update Product' : draftId ? 'Publish Product' : 'Create Product'}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
